import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/mixpanel"

// Pre-construction step 3 — Invite and bid (the prototype's is3pre / is3post /
// is3qa and its pop-ups). Lives inside the step 3 panel; the page's `precon`
// controller swaps the panel when the server answers, so what the person was
// looking at (tab, filters, preview, an open pop-up) is kept per project in
// STATE and put back on reconnect. Tabs, filters and the preview switch here
// with no request; Remind, Answer and Mark called update the page first and
// let the server confirm (rolled back with an error toast if it fails).
const STATE = {}

export default class extends Controller {
  static targets = ["data", "strings", "subject", "message", "qCount", "trackFilters", "remindAll", "trackEmpty",
                    "qaFilters", "qaEmpty", "qaCodeBar", "qaCodeLabel", "tradeNotes", "callFilters", "callEmpty",
                    "addDz", "addFile", "dropDz", "dropFile", "callSlot"]
  static values = { baseUrl: String, projectId: Number, sent: Boolean, pending: Number }

  connect() {
    this.d = this.hasDataTarget ? JSON.parse(this.dataTarget.textContent || "{}") : {}
    this.str = this.hasStringsTarget ? JSON.parse(this.stringsTarget.textContent || "{}") : {}
    this.st = STATE[this.projectIdValue] ||= { tab: null, trackFilter: "all", qaFilter: "all", qaPackage: null, mailFor: "known", callFilter: "all", reopen: null }
    if (this.st.tab) this._setTab(this.st.tab)
    // Subs picked since the panel was last drawn (step 2's Next, a tick in By
    // cost code): open on Send invites, the same invite for just them.
    if (this.sentValue && this.pendingValue > (this.st.pending || 0)) this._setTab("send")
    this.st.pending = this.sentValue ? this.pendingValue : 0
    if (this.st.trackFilter !== "all") this._applyTrackFilter()
    if (this.st.qaFilter !== "all" || this.st.qaPackage) this._applyQaFilter()
    if (this.st.mailFor !== "known") this._applyMailFor()
    const reopen = this.st.reopen
    this.st.reopen = null
    if (reopen) this._open(reopen.modal, reopen)
    else if (this.st.stash) this._adoptStash()
    this._onIntent = () => this._takeIntent()
    this._host?.addEventListener("precon:s3-intent", this._onIntent)
    this._takeIntent()
    if (this.sentValue) { const idle = window.requestIdleCallback || ((f) => setTimeout(f, 300)); idle(() => this._loadCalls().catch(() => {})) }
    this._keydown = (e) => { if (e.key === "Escape" && this._openModal()) { e.stopPropagation(); this._closeTop() } }
    document.addEventListener("keydown", this._keydown, true)
  }

  disconnect() {
    // The panel was swapped (an action elsewhere, a background refresh)
    // while a pop-up was open: keep it, with what was typed and any AI read
    // in progress, for the new panel to take back (_adoptStash).
    const open = this._openModal()
    if (open && !this.st.reopen) {
      this.st.stash = { modal: open.dataset.modal, node: open, flow: { ad: this.ad, dr: this.dr, qa: this.qa, nu: this.nu, tn: this.tn, ae: this.ae, addPending: this._addPending }, pending: this._pendingRun }
    }
    this._host?.removeEventListener("precon:s3-intent", this._onIntent)
    document.removeEventListener("keydown", this._keydown, true)
    clearTimeout(this._poll)
    clearInterval(this._ticker)
  }

  _adoptStash() {
    const st = this.st.stash
    this.st.stash = null
    const fresh = this._modal(st.modal)
    if (!fresh || !st.node) return
    fresh.replaceWith(st.node)
    Object.assign(this, st.flow)
    this._addPending = st.flow.addPending
    if (st.pending?.kind === "ad") this._finishAddRead(st.pending.runId)
    if (st.pending?.kind === "dr") this._finishDropRead(st.pending.runId)
  }

  // ── The page's controller (requests, panels, toasts) ──────────────────
  get _host() { return this.element.closest("[data-controller~='precon']") }
  get precon() { return this._host && this.application.getControllerForElementAndIdentifier(this._host, "precon") }
  _t(key, vars = {}) {
    let s = key.split(".").reduce((o, k) => (o && o[k] !== undefined ? o[k] : null), this.str)
    if (s && typeof s === "object") s = (vars.count === 1 ? s.one : s.other) ?? s.other
    s = s ?? key
    return String(s).replace(/%\{(\w+)\}/g, (_, k) => (vars[k] ?? ""))
  }
  _toast(msg, type) { this.precon?._showToast(msg, type) }
  async _req(method, path, body) { return this.precon._json(method, `${this.baseUrlValue}/${path}`, body) }
  _apply(data, reopen = null) { this.st.reopen = reopen; this.precon?._applyResponse(data) }
  _esc(s) { return String(s ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])) }
  _plural(n, key) { return this._t(key, { count: n }) }

  // The top row's Questions / Addenda buttons (precon#openQuestions / openAddenda).
  _takeIntent() {
    const host = this._host
    const what = host?.dataset.s3Intent
    if (!what) return
    delete host.dataset.s3Intent
    if (what === "qa") { this.st.qaFilter = "all"; this.st.qaPackage = null; this._setTab("qa"); this._applyQaFilter() }
    if (what === "addenda") this._open("addenda", { step: "list" })
  }

  // ── Tabs ──────────────────────────────────────────────────────────────
  showTab(e) {
    this._setTab(e.currentTarget.dataset.tab)
    if (this.st.tab === "qa") track("Precon Questions Opened", { project_id: this.projectIdValue, from: "step3_tab" })
  }

  _setTab(tab) {
    if (!this.element.querySelector(`.tabs3 .tab3[data-tab='${tab}']`)) tab = "bids"
    this.st.tab = tab
    this.element.querySelectorAll(".tabs3 .tab3").forEach(t => { const on = t.dataset.tab === tab; t.classList.toggle("on", on); t.setAttribute("aria-selected", on) })
    this.element.querySelectorAll("[data-precon-tab]").forEach(p => p.classList.toggle("hidden", p.dataset.preconTab !== tab))
  }

  // ── Tracker filters (All / Bids in / Waiting / Declined) ─────────────
  filterTracker(e) {
    this.st.trackFilter = e.currentTarget.dataset.filter
    this._applyTrackFilter()
  }

  _applyTrackFilter() {
    const f = this.st.trackFilter
    if (this.hasTrackFiltersTarget) this.trackFiltersTarget.querySelectorAll(".fchip").forEach(c => c.classList.toggle("on", c.dataset.filter === f))
    let shown = 0
    this.element.querySelectorAll(".t3.trk[data-kind]").forEach(r => { const on = f === "all" || r.dataset.kind === f; r.classList.toggle("hidden", !on); if (on) shown++ })
    if (this.hasTrackEmptyTarget) this.trackEmptyTarget.classList.toggle("hidden", shown > 0)
  }

  // ── Questions filters ────────────────────────────────────────────────
  filterQuestions(e) {
    this.st.qaFilter = e.currentTarget.dataset.filter
    this._applyQaFilter()
  }

  clearQuestionCode() { this.st.qaPackage = null; this._applyQaFilter() }

  _applyQaFilter() {
    const f = this.st.qaFilter
    const pkg = this.st.qaPackage ? String(this.st.qaPackage) : null
    if (this.hasQaFiltersTarget) this.qaFiltersTarget.querySelectorAll(".fchip").forEach(c => c.classList.toggle("on", c.dataset.filter === f))
    let shown = 0
    this.element.querySelectorAll(".qrow[data-question-id]").forEach(r => {
      const on = (f === "all" || r.dataset.status === f) && (!pkg || (r.dataset.packages || "").split(",").includes(pkg))
      r.classList.toggle("hidden", !on)
      if (on) shown++
    })
    if (this.hasQaCodeBarTarget) {
      this.qaCodeBarTarget.classList.toggle("hidden", !pkg)
      const p = pkg && (this.d.packages || []).find(x => String(x.id) === pkg)
      if (p && this.hasQaCodeLabelTarget) this.qaCodeLabelTarget.textContent = this._t("only_code", { label: p.label })
    }
    if (this.hasQaEmptyTarget) {
      this.qaEmptyTarget.classList.toggle("hidden", shown > 0)
      const empty = this.qaEmptyTarget.querySelector(".empty")
      if (empty && this.element.querySelector(".qrow[data-question-id]")) empty.textContent = f === "new" ? empty.dataset.emptyNew : empty.dataset.emptyAll
    }
  }

  // ── What the subs get: A sub you know | A new sub ────────────────────
  mailFor(e) {
    this.st.mailFor = e.currentTarget.dataset.for
    this._applyMailFor()
  }

  _applyMailFor() {
    const f = this.st.mailFor
    this.element.querySelectorAll(".fchip[data-for]").forEach(c => c.classList.toggle("on", c.dataset.for === f))
    this.element.querySelectorAll("[data-s3-for]").forEach(el => el.classList.toggle("hidden", el.dataset.s3For !== f))
  }

  subjectTyped(e) { this.element.querySelectorAll("[data-s3-subject]").forEach(el => { el.textContent = e.currentTarget.value }) }
  messageTyped(e) { this.element.querySelectorAll("[data-s3-message]").forEach(el => { el.textContent = e.currentTarget.value }) }

  async saveDraft() {
    try {
      await this._req("PATCH", "invites/draft", { subject: this.hasSubjectTarget ? this.subjectTarget.value : undefined, message: this.hasMessageTarget ? this.messageTarget.value : undefined })
    } catch (err) { this._toast(err.message, "error") }
  }

  // The date the subs get changed (precon#changeKeyDate saves it): the preview follows.
  dueChanged(e) {
    const v = e.currentTarget.value
    const dt = v ? new Date(v.replace(" ", "T")) : null
    if (!dt || isNaN(dt)) return
    const lang = document.documentElement.lang || "en"
    let day, time
    try {
      day = new Intl.DateTimeFormat(lang, { weekday: "short", month: "short", day: "numeric" }).format(dt)
      time = new Intl.DateTimeFormat(lang, { hour: "numeric", minute: "2-digit" }).format(dt)
    } catch (_) { day = dt.toDateString(); time = dt.toTimeString().slice(0, 5) }
    this.element.querySelectorAll("[data-s3-due]").forEach(el => { el.textContent = `${day} · ${time}` })
  }

  // ── Send the invites ────────────────────────────────────────────────
  async sendInvites(e) {
    const btn = e.currentTarget
    const count = parseInt(btn.dataset.count, 10) || 0
    btn.disabled = true
    const label = btn.querySelector("span")
    const was = label?.textContent
    if (label) label.textContent = this._t("sending")
    const more = this.sentValue
    try {
      const data = await this._req("POST", "invites/send", {
        subject: this.hasSubjectTarget ? this.subjectTarget.value : undefined,
        message: this.hasMessageTarget ? this.messageTarget.value : undefined
      })
      this.st.trackFilter = "all"
      this.st.tab = "bids"
      this.st.pending = 0
      this._apply(data)
      this._toast(this._plural(data.sent, "invites_sent") + (data.skipped ? " " + this._plural(data.skipped, "skipped_no_email") : ""))
      track("Precon Invites Sent", { project_id: this.projectIdValue, sent: data.sent, texted: data.texted || 0, more })
    } catch (err) {
      btn.disabled = false
      if (label) label.textContent = was
      this._toast(err.message, "error")
    }
    return count
  }

  // ── Pop-ups ───────────────────────────────────────────────────────────
  _modal(name) { return this.element.querySelector(`.overlay[data-modal='${name}']`) }
  _openModal() { return this.element.querySelector(".overlay[data-modal]:not(.hidden)") }

  _open(name, opts = {}) {
    if (name === "call") return this._openCalls(opts)
    if (name === "addenda") return this._openAddenda(opts.step || "list")
    const m = this._modal(name)
    if (m) m.classList.remove("hidden")
    return m
  }

  close(e) {
    const overlay = e?.currentTarget?.closest(".overlay") || this._openModal()
    if (overlay) this._closeModal(overlay)
  }

  _closeTop() { const m = this._openModal(); if (m) this._closeModal(m) }

  _closeModal(overlay) {
    overlay.classList.add("hidden")
    clearTimeout(this._poll)
    clearInterval(this._ticker)
    if (overlay.dataset.modal === "addenda" && this._addPending) { const data = this._addPending; this._addPending = null; this._apply(data) }
  }

  stop(e) { e.stopPropagation() }

  openEmail() { this._open("email"); track("Precon Invite Previewed", { project_id: this.projectIdValue, where: "see_the_invite" }) }
  openPhone() { this._open("phone"); track("Precon Invite Previewed", { project_id: this.projectIdValue, where: "phone_view" }) }

  phoneWillBid(e) {
    const b = e.currentTarget
    const on = !b.classList.contains("on")
    b.classList.toggle("on", on)
    b.textContent = on ? b.dataset.yes : b.dataset.no
  }

  // ── Send a reminder (one sub, everyone waiting, or "text the rest") ──
  remind(e) { this._openNudge([e.currentTarget.closest("[data-bidder-id]").dataset.bidderId], null) }

  remindAll() {
    const ids = Array.from(this.element.querySelectorAll(".t3.trk[data-bidder-id]"))
      .filter(r => r.querySelector("[data-action*='precon-invite#remind']")).map(r => r.dataset.bidderId)
    if (ids.length) this._openNudge(ids, null)
  }

  textTheRest(e) {
    const ids = (e.currentTarget.dataset.ids || "").split(",").filter(Boolean)
    if (!ids.length) return
    this._closeModal(this._modal("call"))
    this._openNudge(ids, "text")
  }

  _openNudge(ids, via) {
    const list = ids.map(id => ({ id, ...(this.d.bidders || {})[id] })).filter(b => b.name)
    if (!list.length) return
    this.nu = { list, via, msg: null }
    this._renderNudge()
    this._open("nudge")
  }

  _nudgeStd() {
    const list = this.nu.list
    const due = (this.d.due || "").replace(" · ", ` ${this._t("at")} `)
    const sig = `${this._t("nu_thanks")}\n${this.d.me}\n${this.d.company}`
    if (list.length === 1) {
      const b = list[0]
      const first = String(b.contact || "").split(" ")[0] || this._t("there")
      return this._t(b.will ? "nu_std_yes" : "nu_std_one", { first, code: b.code, pkg: b.pkg, project: this.d.project, due }) + "\n\n" + sig
    }
    return this._t("nu_std_many", { project: this.d.project, due }) + "\n\n" + sig
  }

  _nudgeHow(b, want) {
    const email = !!b.email, text = !!b.text
    if (want === "both") return email && text ? "both" : (email ? "email" : (text ? "text" : ""))
    if (want === "text") return text ? "text" : (email ? "email" : "")
    return email ? "email" : (text ? "text" : "")
  }

  _renderNudge() {
    const m = this._modal("nudge"); const nu = this.nu; const list = nu.list
    const one = list.length === 1 ? list[0] : null
    const nEmail = list.filter(b => b.email).length, nText = list.filter(b => b.text).length
    const ok = { email: nEmail > 0, text: nText > 0, both: one ? !!(one.email && one.text) : nEmail > 0 && nText > 0 }
    const want = nu.via && ok[nu.via] ? nu.via : (ok.email ? "email" : (ok.text ? "text" : ""))
    nu.want = want
    const q = (k) => m.querySelector(`[data-nu='${k}']`)
    q("title").textContent = one ? this._t("nu_title_one", { name: one.name }) : this._t("nu_title_many", { count: list.length })
    const names = list.map(b => b.name)
    q("sub").textContent = one ? `${one.code} ${one.pkg} · ${one.contact}`.trim() : (names.length > 4 ? this._t("names_more", { names: names.slice(0, 4).join(", "), count: names.length - 4 }) : this._t("names_and", { names: names.slice(0, -1).join(", "), last: names[names.length - 1] }))
    m.querySelectorAll(".fchip[data-via]").forEach(c => { const on = c.dataset.via === want; c.classList.toggle("on", on); c.setAttribute("aria-pressed", on); c.disabled = !ok[c.dataset.via] })
    let where
    if (one) {
      const parts = []
      if (want === "email" || want === "both") parts.push(this._t("nu_email_to", { email: one.email }))
      if (want === "text" || want === "both") parts.push(this._t("nu_text_to"))
      where = parts.join(` ${this._t("and")} `)
      where = where ? where.charAt(0).toUpperCase() + where.slice(1) + "." : ""
      if (!one.text) where += (where ? " " : "") + this._t("nu_no_texts")
      if (!one.email) where += (where ? " " : "") + this._t("nu_no_email")
    } else {
      const tn = list.filter(b => ["text", "both"].includes(this._nudgeHow(b, want))).length
      const en = list.filter(b => ["email", "both"].includes(this._nudgeHow(b, want))).length
      const allOf = (x) => (x === list.length ? (x === 2 ? this._t("both_of") : this._t("all_of", { count: x })) : this._t("x_of", { x, count: list.length }))
      const bits = []
      if (en) bits.push(this._t("nu_email_goes", { who: allOf(en) }))
      if (tn) bits.push(this._t("nu_text_goes", { who: allOf(tn) }))
      where = bits.join(", ") + "." + ((want === "text" || want === "both") && tn < list.length ? " " + this._t("nu_rest_email") : "")
      where = where.charAt(0).toUpperCase() + where.slice(1)
    }
    q("where").textContent = where
    const std = this._nudgeStd()
    const msg = nu.msg == null ? std : nu.msg
    const ta = q("msg")
    if (ta.value !== msg) ta.value = msg
    q("hint").textContent = this._t("nu_link_end") + (want === "text" || want === "both" ? " " + this._t("nu_stop_end") : "")
    q("reset").classList.toggle("hidden", !(nu.msg != null && nu.msg !== std))
    const sendable = list.filter(b => this._nudgeHow(b, want))
    nu.sendable = sendable
    q("send").disabled = !sendable.length || !String(msg).trim()
    q("sendLabel").textContent = one ? this._t("nu_send_one") : this._plural(sendable.length, "nu_send_many")
  }

  nudgeVia(e) { this.nu.via = e.currentTarget.dataset.via; this._renderNudge() }
  nudgeTyped(e) { this.nu.msg = e.currentTarget.value; this._renderNudge() }
  nudgeReset() { this.nu.msg = null; this._renderNudge() }

  async nudgeSend() {
    const nu = this.nu
    const ids = nu.sendable.map(b => b.id)
    const message = this._modal("nudge").querySelector("[data-nu='msg']").value.trim()
    if (!ids.length || !message) return
    this._closeModal(this._modal("nudge"))
    // Instantly: the rows say Reminded, the button counts down.
    const undo = []
    ids.forEach(id => {
      const row = this.element.querySelector(`.t3.trk[data-bidder-id='${id}']`)
      const btn = row?.querySelector("[data-action*='precon-invite#remind']")
      if (btn) { const span = document.createElement("span"); span.className = "muted"; span.textContent = this._t("reminded"); btn.replaceWith(span); undo.push(() => span.replaceWith(btn)) }
    })
    this._paintRemindAll()
    const one = nu.list.length === 1 ? nu.list[0] : null
    this._toast(one ? this._t("reminder_sent_one", { name: one.name, via: this._t(`via_${this._nudgeHow(one, nu.want)}`) }) : this._plural(ids.length, "reminders_sent"))
    try {
      const data = await this._req("POST", "invites/remind", { ids, via: nu.want, message })
      this._apply(data)
      track("Precon Invite Reminded", { project_id: this.projectIdValue, subs: ids.length, via: nu.want })
    } catch (err) {
      undo.forEach(f => f()); this._paintRemindAll()
      this._toast(err.message, "error")
    }
  }

  _paintRemindAll() {
    if (!this.hasRemindAllTarget) return
    const n = this.element.querySelectorAll(".t3.trk [data-action*='precon-invite#remind']").length
    const btn = this.remindAllTarget
    btn.disabled = n === 0
    btn.querySelector("span").textContent = n ? btn.dataset.labelSome.replace("%{n}", n) : btn.dataset.labelNone
  }

  // ── Call list ─────────────────────────────────────────────────────────
  openCallList() { this._openCalls({}); track("Precon Call List Opened", { project_id: this.projectIdValue }) }

  // The call list is the biggest pop-up, so it isn't in the panel: it loads
  // in the background once the panel is up, and again after a change.
  _loadCalls() {
    if (!this.hasCallSlotTarget || !this.sentValue) return Promise.resolve()
    if (!this._callsLoading) {
      this._callsLoading = this._req("GET", "invites/call_list").then(data => {
        if (this.hasCallSlotTarget) this.callSlotTarget.innerHTML = data.html
      }).catch(err => { this._callsLoading = null; throw err })
    }
    return this._callsLoading
  }

  async _openCalls(opts) {
    try { await this._loadCalls() } catch (err) { this._toast(err.message, "error"); return }
    const m = this._modal("call")
    if (!m) return
    if (opts.filter) this.st.callFilter = opts.filter
    this._applyCallFilter()
    m.classList.remove("hidden")
  }

  filterCalls(e) { this.st.callFilter = e.currentTarget.dataset.filter; this._applyCallFilter() }

  _applyCallFilter() {
    const m = this._modal("call"); const f = this.st.callFilter || "all"
    m.querySelectorAll("[data-precon-invite-target='callFilters'] .fchip").forEach(c => { const on = c.dataset.filter === f; c.classList.toggle("on", on); c.setAttribute("aria-pressed", on) })
    const rows = Array.from(m.querySelectorAll(".clrow[data-sub-id]"))
    let shown = 0
    rows.forEach(r => { const on = f === "all" || (f === "todo" ? r.dataset.called !== "1" : r.dataset.called === "1"); r.classList.toggle("hidden", !on); if (on) shown++ })
    const head = m.querySelector(".clhead")
    if (head) head.classList.toggle("hidden", shown === 0)
    if (this.hasCallEmptyTarget) {
      const el = this.callEmptyTarget
      el.classList.toggle("hidden", shown > 0)
      el.textContent = !rows.length ? el.dataset.textNone : (f === "done" ? el.dataset.textDone : el.dataset.textTodo)
    }
  }

  markCalled(e) {
    const row = e.currentTarget.closest(".clrow")
    this._modal("call").querySelectorAll(".clrow .clnote:not(.hidden)").forEach(n => this._closeCallNote(n.closest(".clrow")))
    row.querySelector(".clnote").classList.remove("hidden")
    e.currentTarget.classList.add("hidden")
    row.querySelector(".clnote input").focus()
  }

  _closeCallNote(row) {
    row.querySelector(".clnote").classList.add("hidden")
    row.querySelector("[data-action*='markCalled']")?.classList.remove("hidden")
    row.querySelectorAll(".clnote .fchip").forEach(c => c.classList.remove("on"))
    row.querySelector(".clnote input").value = ""
  }

  callPick(e) {
    const row = e.currentTarget.closest(".clrow")
    row.querySelector(".clnote input").value = e.currentTarget.dataset.pick
    row.querySelectorAll(".clnote .fchip").forEach(c => c.classList.toggle("on", c === e.currentTarget))
  }

  callCancel(e) { this._closeCallNote(e.currentTarget.closest(".clrow")) }
  callKey(e) { if (e.key === "Enter") { e.preventDefault(); this.callSave(e) } }

  async callSave(e) {
    const row = e.currentTarget.closest(".clrow")
    const note = row.querySelector(".clnote input").value.trim()
    // Instantly: the row shows as called, with the note.
    this._closeCallNote(row)
    row.classList.add("called"); row.dataset.called = "1"
    let line = row.querySelector(".clcall")
    const added = !line
    if (!line) { line = document.createElement("div"); line.className = "clcall"; row.querySelector(".clmain").after(line) }
    const was = line.innerHTML
    line.innerHTML = `<span>${this._esc(this._t("called_now", { who: this.d.me }))}</span>${note ? `<span class="clsaid">“${this._esc(note)}”</span>` : ""}`
    this._applyCallFilter()
    const name = row.querySelector(".strong")?.textContent || ""
    this._toast(this._t(note ? "call_saved_note" : "call_saved", { name, note }))
    try {
      const data = await this._req("POST", "calls", { sub_id: row.dataset.subId, note })
      track("Precon Sub Called", { project_id: this.projectIdValue, with_note: !!note })
      this._apply(data, { modal: "call", filter: this.st.callFilter })
    } catch (err) {
      if (added) line.remove(); else line.innerHTML = was
      this._toast(err.message, "error")
    }
  }

  async copyPhone(e) {
    const num = e.currentTarget.dataset.phone
    try { await navigator.clipboard.writeText(num); this._toast(this._t("copied", { phone: num })) } catch (_) { this._toast(num) }
  }

  // ── Answer a question ─────────────────────────────────────────────────
  openAnswer(e) {
    const id = e.currentTarget.dataset.questionId
    const q = (this.d.questions || {})[id]
    if (!q) return
    this.qa = { id, q, about: JSON.parse(JSON.stringify(q.about || [{ t: "none" }])), aud: q.answered ? q.audience : "just", addOn: !!q.for_addendum, mail: false, pick: false, pickQ: "", draft: q.answer || "" }
    const m = this._modal("answer")
    m.querySelector("[data-qa='draft']").value = this.qa.draft
    m.querySelector("[data-qa='add']").checked = this.qa.addOn
    m.querySelector("[data-qa='add']").onchange = (ev) => { this.qa.addOn = ev.target.checked }
    this._renderAnswer()
    this._open("answer")
    track("Precon Question Opened", { project_id: this.projectIdValue, answered: !!q.answered })
  }

  _pkg(id) { return (this.d.packages || []).find(p => String(p.id) === String(id)) }

  _cover(kind) {
    const ab = this.qa.about
    const pkgs = this.d.packages || []
    if (kind === "job") return { pids: pkgs.map(p => p.id), divs: [] }
    const codePids = ab.filter(x => x.t === "package").map(x => String(x.id))
    if (kind === "package") return { pids: pkgs.filter(p => codePids.includes(String(p.id))).map(p => p.id), divs: [] }
    const divs = [...new Set(ab.filter(x => x.t === "division").map(x => String(x.id)).concat(pkgs.filter(p => codePids.includes(String(p.id))).flatMap(p => p.divs)))]
    return { pids: pkgs.filter(p => p.divs.some(d => divs.includes(d))).map(p => p.id), divs }
  }

  _subsOn(pids) { const s = new Set(); pids.forEach(id => (this._pkg(id)?.subs || []).forEach(x => s.add(x))); return s.size }

  _renderAnswer() {
    const m = this._modal("answer"); const qa = this.qa; const q = qa.q
    const g = (k) => m.querySelector(`[data-qa='${k}']`)
    const done = !!q.answered
    const poss = (n) => this._t(/s$/i.test(n) ? "poss_s" : "poss", { name: n })
    g("title").textContent = this._t("qa_title", { from: q.from })
    g("subline").textContent = [q.code ? this._t("qa_they_bid", { code: q.code }) : null, q.when, this._t("qa_only_you_see")].filter(Boolean).join(" · ")
    g("q").textContent = q.body
    // What it's about
    const labelOf = (x) => x.t === "job" ? this._t("about_job") : (x.t === "none" ? this._t("about_general") : (x.t === "division" ? ((this.d.divisions || []).find(d => d.id === String(x.id))?.label || x.id) : (this._pkg(x.id)?.label || "")))
    const xSvg = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 18 18 6M6 6l12 12"></path></svg>`
    g("chips").innerHTML = qa.about.map((x, i) => `<span class="fchipn"><span>${this._esc(labelOf(x))}</span><button class="qtx" type="button" data-i="${i}" aria-label="${this._esc(this._t("remove_x", { x: labelOf(x) }))}">${xSvg}</button></span>`).join("") +
      `<button class="fchip fadd" type="button" data-add>${this._esc(qa.pick ? this._t("done") : this._t("add_plus"))}</button>`
    g("chips").querySelectorAll(".qtx").forEach(b => b.addEventListener("click", () => { qa.about.splice(parseInt(b.dataset.i, 10), 1); if (!qa.about.length) qa.about = [{ t: "none" }]; this._renderAnswer() }))
    g("chips").querySelector("[data-add]").addEventListener("click", () => { qa.pick = !qa.pick; qa.pickQ = ""; const inp = g("pick").querySelector("input"); if (inp) inp.value = ""; this._renderAnswer() })
    g("pick").classList.toggle("hidden", !qa.pick)
    if (qa.pick) this._renderPick()
    // Who sees the answer
    const covT = this._cover("package"), covD = this._cover("division"), covJ = this._cover("job")
    const nT = this._subsOn(covT.pids), nD = this._subsOn(covD.pids), nJ = this._subsOn(covJ.pids)
    const isNone = qa.about.some(x => x.t === "none")
    const codesTxt = covT.pids.map(id => this._pkg(id)?.label).join(", ")
    const divLabels = covD.divs.slice().sort()
    const divsTxt = divLabels.length === 1 ? this._t("division_one", { label: ((this.d.divisions || []).find(d => d.id === divLabels[0])?.label || divLabels[0]).replace(/^\S+\s/, "") }) : this._t("divisions_many", { list: this._join(divLabels) })
    const hid = this._t("name_hidden", { poss: poss(q.from) })
    const subs = (n) => this._plural(n, "n_subs")
    const codes = (n) => this._plural(n, "n_codes")
    const avail = [["just", this._t("aud_just", { from: q.from }), this._t("aud_just_sub")]]
    if (covT.pids.length) avail.push(["package", covT.pids.length === 1 ? this._t("aud_everyone", { what: codesTxt }) : this._t("aud_everyone", { what: this._t("these_codes", { codes: codes(covT.pids.length) }) }), [(covT.pids.length > 1 ? codesTxt : null), subs(nT), hid].filter(Boolean).join(" · ")])
    if (covD.divs.length && covD.pids.length > covT.pids.length) avail.push(["division", this._t("aud_everyone", { what: divsTxt }), `${this._t("subs_on", { subs: subs(nD), codes: codes(covD.pids.length) })} · ${hid}`])
    avail.push(["job", this._t("aud_job"), `${this._t("subs_on", { subs: subs(nJ), codes: codes(covJ.pids.length) })} · ${hid}`])
    if (!avail.some(o => o[0] === qa.aud)) qa.aud = "just"
    g("auds").innerHTML = avail.map(o => `<button class="qaopt ${qa.aud === o[0] ? "on" : ""}" type="button" role="radio" aria-checked="${qa.aud === o[0]}" data-aud="${o[0]}"><span class="qadot"></span><span><span class="block">${this._esc(o[1])}</span><span class="muted block">${this._esc(o[2])}</span></span></button>`).join("")
    g("auds").querySelectorAll(".qaopt").forEach(b => b.addEventListener("click", () => { qa.aud = b.dataset.aud; this._renderAnswer() }))
    g("warn").classList.toggle("hidden", !(qa.aud === "just" && !isNone))
    g("mailNote").textContent = qa.aud === "just" ? this._t("mail_note_just") : this._t("mail_note_many")
    // Addendum
    g("addWrap").classList.toggle("hidden", !!q.in_addendum)
    g("inAdd").classList.toggle("hidden", !q.in_addendum)
    g("inAdd").textContent = q.in_add_text || ""
    // The draft
    const draft = String(qa.draft || "").trim()
    g("draftHint").textContent = done ? this._t("draft_hint_done", { when: q.answered_when, by: q.by || this.d.me }) : ""
    // The email they get
    const changed = done && draft !== String(q.answer || "").trim()
    const earlier = (q.history || []).concat(changed ? [{ answer: q.answer, day: q.answered_day }] : [])
    const chain = [{ who: this._t("chain_yours", { day: q.day }), text: q.body, cls: "q" }]
      .concat(earlier.map(x => ({ who: this._t("chain_earlier", { company: poss(this.d.company), day: x.day }), text: x.answer, cls: "a old" })))
      .concat([{ who: done && !changed ? this._t("chain_answer_day", { company: poss(this.d.company), day: q.answered_day }) : this._t(earlier.length ? "chain_updated_today" : "chain_answer_today", { company: poss(this.d.company) }), text: draft || this._t("chain_placeholder"), cls: "a" }])
    g("chain").innerHTML = chain.map(c => `<div class="qmsg ${c.cls}"><span class="qmwho">${this._esc(c.who)}</span><span>${this._esc(c.text)}</span></div>`).join("")
    g("mailSubject").textContent = this._t(earlier.length ? "mail_subject_updated" : "mail_subject", { project: this.d.project })
    g("mailToggle").textContent = qa.mail ? this._t("hide_email") : this._t("see_email")
    g("mail").classList.toggle("hidden", !qa.mail)
    g("mailOthers").classList.toggle("hidden", qa.aud === "just")
    g("mailOthers").textContent = this._t("mail_others", { poss: poss(q.from) })
    const send = g("send")
    send.textContent = done ? this._t("send_update") : this._t("send_answer")
    send.disabled = !draft
  }

  // The Questions chips and the tab's count follow the rows at once.
  _recountQa() {
    const rows = Array.from(this.element.querySelectorAll(".qrow[data-question-id]"))
    const n = { all: rows.length, new: rows.filter(r => r.dataset.status === "new").length }
    n.answered = n.all - n.new
    if (this.hasQaFiltersTarget) this.qaFiltersTarget.querySelectorAll(".fchip[data-label]").forEach(c => { c.textContent = `${c.dataset.label} (${n[c.dataset.filter] ?? 0})` })
    if (this.hasQCountTarget) { this.qCountTarget.classList.toggle("hot", n.new > 0); this.qCountTarget.textContent = n.new ? this._t("n_new", { count: n.new }) : String(n.all) }
  }

  _join(a) { return a.length > 1 ? `${a.slice(0, -1).join(", ")} ${this._t("and")} ${a[a.length - 1]}` : a.join("") }

  _renderPick() {
    const m = this._modal("answer"); const qa = this.qa
    const pq = (qa.pickQ || "").trim().toLowerCase()
    const match = (s) => !pq || s.toLowerCase().includes(pq)
    const has = (t, id) => qa.about.some(x => x.t === t && String(x.id ?? "") === String(id ?? ""))
    const check = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m4.5 12.75 6 6 9-13.5"></path></svg>`
    const opt = (t, id, label) => `<button class="qpopt ${has(t, id) ? "on" : ""}" type="button" data-t="${t}" data-id="${this._esc(id ?? "")}"><span>${this._esc(label)}</span>${has(t, id) ? check : ""}</button>`
    const rows = []
    if (match(this._t("about_job") + " " + this._t("pick_job_words"))) rows.push(opt("job", "", this._t("about_job")))
    if (match(this._t("pick_general") + " " + this._t("pick_general_words"))) rows.push(opt("none", "", this._t("pick_general")))
    const dv = (this.d.divisions || []).filter(x => match(x.label))
    if (dv.length) { rows.push(`<div class="qphead">${this._esc(this._t("pick_divisions"))}</div>`); dv.forEach(x => rows.push(opt("division", x.id, x.label))) }
    const cs = (this.d.packages || []).filter(p => match(p.label))
    if (cs.length) { rows.push(`<div class="qphead">${this._esc(this._t("pick_codes"))}</div>`); cs.forEach(p => rows.push(opt("package", p.id, p.label))) }
    const list = m.querySelector("[data-qa='picklist']")
    list.innerHTML = rows.join("")
    m.querySelector("[data-qa='pickEmpty']").classList.toggle("hidden", rows.length > 0)
    list.querySelectorAll(".qpopt").forEach(b => b.addEventListener("click", () => {
      const t = b.dataset.t, id = b.dataset.id
      if (t === "job" || t === "none") qa.about = has(t) ? [{ t: "none" }] : [{ t }]
      else qa.about = has(t, id) ? qa.about.filter(x => !(x.t === t && String(x.id) === id)) : qa.about.filter(x => x.t !== "job" && x.t !== "none").concat([{ t, id }])
      if (!qa.about.length) qa.about = [{ t: "none" }]
      this._renderAnswer()
    }))
  }

  qaPickFilter(e) { this.qa.pickQ = e.currentTarget.value; this._renderPick() }
  qaTyped(e) { this.qa.draft = e.currentTarget.value; this._renderAnswer() }
  qaMailToggle() { this.qa.mail = !this.qa.mail; this._renderAnswer() }

  async qaSend() {
    const qa = this.qa
    const answer = String(qa.draft || "").trim()
    if (!answer) return
    const q = qa.q
    const covT = this._cover("package"), covD = this._cover("division"), covJ = this._cover("job")
    const toWho = qa.aud === "just" ? this._t("to_only", { from: q.from }) : (qa.aud === "package" ? this._t("to_subs_bidding", { subs: this._plural(this._subsOn(covT.pids), "n_subs"), what: covT.pids.map(id => this._pkg(id)?.label).join(", ") })
      : (qa.aud === "division" ? this._t("to_subs_bidding", { subs: this._plural(this._subsOn(covD.pids), "n_subs"), what: this._join(covD.divs) }) : this._t("to_all_job", { subs: this._plural(this._subsOn(covJ.pids), "n_subs") })))
    const poss = this._t(/s$/i.test(q.from) ? "poss_s" : "poss", { name: q.from })
    const msg = this._t(q.answered ? "update_sent" : "answer_sent", { who: toWho }) + (qa.aud === "just" ? "" : " " + this._t("name_stays_hidden", { poss })) + (qa.addOn && !q.in_addendum ? " " + this._t("goes_in_addendum") : "")
    this._closeModal(this._modal("answer"))
    // Instantly: the row shows as answered.
    const row = this.element.querySelector(`.qrow[data-question-id='${qa.id}']`)
    const was = row?.innerHTML
    if (row) {
      row.classList.remove("isnew"); row.dataset.status = "answered"
      const badge = row.querySelector(".badge")
      if (badge) { badge.className = `badge ${qa.aud === "just" ? "b-gray" : "b-green"}`; badge.textContent = this._t(`answered_${qa.aud}`) }
      const btn = row.querySelector(".btn"); if (btn) { btn.classList.replace("btn-primary", "btn-secondary"); btn.textContent = this._t("view") }
    }
    this._recountQa()
    this._toast(msg)
    try {
      const data = await this._req("PATCH", `questions/${qa.id}`, { answer, audience: qa.aud, about: qa.about, for_addendum: qa.addOn ? "1" : "0" })
      track(qa.aud === "just" ? "Precon Question Answered" : "Precon Answer Published", { project_id: this.projectIdValue, audience: qa.aud, for_addendum: qa.addOn, corrected: !!q.answered })
      this._apply(data)
    } catch (err) {
      if (row && was !== undefined) row.innerHTML = was
      this._toast(err.message, "error")
    }
  }

  // ── Addenda ───────────────────────────────────────────────────────────
  openAddendaNew() { this._openAddenda("upload"); track("Precon Addendum Started", { project_id: this.projectIdValue }) }
  addendaStart() { this._openAddenda("upload"); track("Precon Addendum Started", { project_id: this.projectIdValue }) }
  addendaBack() { this._addStep("list") }
  addendaBackUpload() { this._addStep("upload") }

  _openAddenda(step) {
    const m = this._modal("addenda")
    if (!m) return
    if (step === "upload") this.ad = { files: [], docId: null }
    this._addStep(step)
    m.classList.remove("hidden")
  }

  _addStep(step) {
    const m = this._modal("addenda")
    m.querySelectorAll("[data-ad-step]").forEach(s => s.classList.toggle("hidden", s.dataset.adStep !== step))
    const title = m.querySelector("[data-ad='title']"), sub = m.querySelector("[data-ad='subtitle']")
    if (step === "list") { title.textContent = this._t("addenda"); sub.textContent = this._t("ad_list_sub") } else if (step !== "done") {
      title.textContent = this._t("ad_new_title", { label: this.ad?.label || this.d.next_addendum })
      sub.textContent = this._t(`ad_step_${step}`)
    }
    if (step === "upload") this._renderAddFiles()
  }

  addendaPick() { this.addFileTarget.click() }
  addendaPicked(e) { this._addUpload(Array.from(e.target.files || [])); e.target.value = "" }
  addendaDrop(e) { e.preventDefault(); this.dzLeave(e); this._addUpload(Array.from(e.dataTransfer?.files || [])) }
  dzOver(e) { e.preventDefault(); e.currentTarget.classList.add("over") }
  dzLeave(e) { e.currentTarget.classList.remove("over") }

  _size(n) { return n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB` }

  _chip(f, removeAction) {
    const check = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m4.5 12.75 6 6 9-13.5"></path></svg>`
    const x = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 18 18 6M6 6l12 12"></path></svg>`
    return `<span class="fchipn ${f.up ? "up" : ""}">${f.up ? `<span class="spin2"></span>` : check}<span class="trunc">${this._esc(f.name)}</span><span class="muted">${this._size(f.size)}</span>${f.up ? "" : `<button class="fx" type="button" data-remove="${removeAction}" aria-label="${this._esc(this._t("remove_x", { x: f.name }))}">${x}</button>`}</span>`
  }

  _renderAddFiles() {
    const m = this._modal("addenda")
    const box = m.querySelector("[data-ad='files']")
    box.innerHTML = (this.ad?.files || []).map(f => this._chip(f, "add")).join("")
    box.querySelectorAll("[data-remove]").forEach(b => b.addEventListener("click", () => this._addRemove()))
    m.querySelector("[data-ad='read']").disabled = !(this.ad?.docId && !this.ad.files.some(f => f.up))
  }

  async _upload(file) {
    const presign = await this.precon._json("POST", this.d.presign_url, { files: [{ filename: file.name, content_type: file.type || "application/pdf", byte_size: file.size }] })
    const u = presign.uploads[0]
    const res = await fetch(u.upload_url, { method: "PUT", body: file, headers: { "Content-Type": u.content_type || file.type || "application/octet-stream" } })
    if (!res.ok) throw new Error(this._t("upload_failed", { name: file.name }))
    return { cache_name: u.cache_name, filename: file.name, content_type: file.type, byte_size: file.size }
  }

  async _addUpload(files) {
    const file = files.find(f => /pdf$/i.test(f.type) || /\.pdf$/i.test(f.name))
    if (!file) { if (files.length) this._toast(this._t("pdf_only"), "error"); return }
    if (this.ad.docId) await this._addRemove(true)
    this.ad.files = [{ name: file.name, size: file.size, up: true }]
    this._renderAddFiles()
    try {
      const up = await this._upload(file)
      const reg = await this.precon._json("POST", `${this.baseUrlValue}/documents/register`, { kind: "addendum", files: [up] })
      this.ad.docId = reg.documents?.[0]?.id
      this.ad.registered = reg
      this.ad.files = [{ name: file.name, size: file.size, up: false }]
      this._renderAddFiles()
    } catch (err) {
      this.ad.files = []
      this._renderAddFiles()
      this._toast(err.message, "error")
    }
  }

  async _addRemove(quiet) {
    const id = this.ad?.docId
    this.ad.files = []; this.ad.docId = null
    this._renderAddFiles()
    if (id) { try { await this.precon._json("DELETE", `${this.baseUrlValue}/documents/${id}`) } catch (err) { if (!quiet) this._toast(err.message, "error") } }
  }

  async addendaRead() {
    if (!this.ad?.docId) return
    this._readAddendum(this.ad.docId, this.ad.files[0]?.name)
  }

  // From the Addenda list: an addendum that was uploaded but not sent yet.
  addendumReview(e) {
    this.ad = { files: [], docId: e.currentTarget.dataset.documentId }
    this._readAddendum(this.ad.docId, "")
  }

  async _readAddendum(docId, name) {
    this._addStep("reading")
    const m = this._modal("addenda")
    m.querySelector("[data-ad='readingTitle']").textContent = this._t("ad_reading", { name: name || this._t("the_addendum") })
    const msgs = this.str.ad_read_msgs || []
    let i = 0, pct = 5
    const msgEl = m.querySelector("[data-ad='readingMsg']"), bar = m.querySelector("[data-ad='bar']")
    msgEl.textContent = msgs[0] || ""
    clearInterval(this._ticker)
    this._ticker = setInterval(() => { pct = Math.min(92, pct + (92 - pct) * 0.08); bar.style.width = `${pct}%`; if (pct > (i + 1) * 22 && i < msgs.length - 2) { i++; msgEl.textContent = msgs[i] } }, 400)
    try {
      const run = await this._req("POST", `addenda/${docId}/read`, {})
      await this._finishAddRead(run.run_id)
    } catch (err) {
      clearInterval(this._ticker)
      this._toast(err.message, "error")
      this._addStep("upload")
    }
  }

  async _finishAddRead(runId) {
    this._pendingRun = { kind: "ad", runId }
    try {
      const result = await this._pollRun(runId)
      this._pendingRun = null
      clearInterval(this._ticker)
      const bar = this._modal("addenda")?.querySelector("[data-ad='bar']")
      if (bar) bar.style.width = "100%"
      this._renderReview(result.review)
    } catch (err) {
      this._pendingRun = null
      clearInterval(this._ticker)
      this._toast(err.message, "error")
      this._addStep("upload")
    }
  }

  _pollRun(id) {
    return new Promise((resolve, reject) => {
      const tick = async () => {
        try {
          const data = await this._req("GET", `addenda/runs/${id}`)
          if (data.status === "done") return resolve(data)
          if (data.status === "failed") return reject(new Error(data.error || this._t("ai_failed")))
        } catch (err) { /* keep trying */ }
        this._poll = setTimeout(tick, 2000)
      }
      this._poll = setTimeout(tick, 1500)
    })
  }

  _renderReview(r) {
    this.ad.review = r
    this.ad.label = r.label
    const m = this._modal("addenda")
    const g = (k) => m.querySelector(`[data-ad='${k}']`)
    const n = r.changes.length
    g("foundMsg").textContent = n ? this._t("ad_found", { count: n, file: r.file }) : this._t("ad_found_none", { file: r.file })
    const arrow = `<svg class="arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"></path></svg>`
    const cal = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"></path></svg>`
    g("changes").innerHTML = r.changes.map(c => `
      <div class="chg ${c.on === false ? "off" : ""}" data-id="${this._esc(c.id)}">
        <input id="chg-${this._esc(c.id)}" type="checkbox" ${c.on === false ? "" : "checked"}>
        <label class="chgmain" for="chg-${this._esc(c.id)}"><span class="chgtop"><span class="badge ${c.tag_cls}">${this._esc(c.tag)}</span><span>${this._esc(c.text)}</span></span><span class="muted">${this._esc(c.sub || "")}</span></label>
        ${c.kind === "date" ? `<div class="chgdate"><s>${this._esc(c.from)}</s>${arrow}<span class="dp">${cal}<input class="inp dpi" type="text" value="${this._esc(c.to)}" placeholder="mm/dd/yyyy h:mm AM" aria-label="${this._esc(this._t("new_date_for", { what: c.text }))}" data-controller="datepicker" data-datepicker-enable-time-value="true" data-datepicker-format-value="Y-m-d H:i" data-datepicker-alt-format-value="m/d/Y h:i K"></span><span class="muted">${this._esc(c.to_nice || "")}</span></div>` : ""}
      </div>`).join("")
    g("changes").querySelectorAll(".chg input[type=checkbox]").forEach(cb => cb.addEventListener("change", () => cb.closest(".chg").classList.toggle("off", !cb.checked)))
    g("titleInput").value = r.title || ""
    g("note").value = r.note || ""
    g("who").textContent = r.sent ? this._t("ad_who_all", { count: r.everyone }) : this._t("ad_who_invite", { count: r.everyone })
    g("trades").innerHTML = r.trades.map(t => `<div class="trl ${t.hit ? "hit" : ""}" data-package-id="${t.package_id}" data-hit="${t.hit ? 1 : 0}"><span class="trc">${this._esc(t.label)}</span><span class="trt">${this._esc(t.text)}</span></div>`).join("") || `<div class="muted">${this._esc(this._t("ad_no_trades"))}</div>`
    g("mailHint").textContent = this._t("ad_mail_hint", { label: r.label, project: this.d.project })
    g("sendLabel").textContent = r.sent ? this._t("ad_send_all", { label: r.label, count: r.everyone }) : this._t("ad_save_with_invite", { label: r.label })
    this._addStep("review")
  }

  async addendaSend(e) {
    const m = this._modal("addenda")
    const r = this.ad.review
    const changes = {}
    m.querySelectorAll("[data-ad='changes'] .chg").forEach(row => {
      changes[row.dataset.id] = { on: row.querySelector("input[type=checkbox]").checked ? "1" : "0", to: row.querySelector(".chgdate input")?.value || undefined }
    })
    const trades = {}
    m.querySelectorAll("[data-ad='trades'] .trl[data-hit='1']").forEach(t => { trades[t.dataset.packageId] = t.querySelector(".trt").textContent })
    const btn = e.currentTarget
    btn.disabled = true
    try {
      const data = await this._req("POST", `addenda/${r.document_id}/send`, { title: m.querySelector("[data-ad='titleInput']").value, note: m.querySelector("[data-ad='note']").value, changes, trades })
      this._addPending = data
      this._renderDone(data.done)
      this._toast(data.done.title)
      track("Precon Addendum Sent", { project_id: this.projectIdValue, changes: Object.values(changes).filter(c => c.on === "1").length, sent: !!r.sent })
    } catch (err) {
      this._toast(err.message, "error")
    } finally { btn.disabled = false }
  }

  _renderDone(done) {
    const m = this._modal("addenda")
    const g = (k) => m.querySelector(`[data-ad='${k}']`)
    m.querySelector("[data-ad='title']").textContent = this._t("ad_is_out", { label: this.ad.label })
    m.querySelector("[data-ad='subtitle']").textContent = this._t("ad_step_done")
    g("doneTitle").textContent = done.title
    g("doneSub").textContent = done.sub
    g("doneText").textContent = (done.lines || []).map(x => `• ${x}`).join("\n") || this._t("ad_nothing_changed")
    const next = done.next || []
    g("nextWrap").classList.toggle("hidden", !next.length)
    g("nextList").innerHTML = next.map(n => `<div class="nxrow"><span><b>${this._esc(this._t("step_n", { n: n.step, name: n.name }))}</b> · ${this._esc(n.text)}</span><span class="att-tag">${this._esc(this._plural(n.count, "n_to_do"))}</span></div>`).join("")
    const go = g("goNext")
    go.classList.toggle("hidden", !next.length)
    if (next.length) { go.dataset.step = next[0].step; g("goNextLabel").textContent = this._t("go_to_step", { n: next[0].step }) }
    m.querySelectorAll("[data-ad-step]").forEach(s => s.classList.toggle("hidden", s.dataset.adStep !== "done"))
  }

  addendaGoNext(e) {
    const step = parseInt(e.currentTarget.dataset.step, 10)
    this._closeModal(this._modal("addenda"))
    this.precon?.showStep(step, true)
  }

  async addendumRemind(e) {
    const btn = e.currentTarget
    const span = document.createElement("span"); span.className = "muted"; span.textContent = this._t("reminder_sent")
    btn.replaceWith(span)
    try {
      const data = await this._req("POST", `addenda/${btn.dataset.documentId}/remind`, {})
      this._toast(this._plural(data.reminded, "ad_reminded"))
      track("Precon Addendum Reminded", { project_id: this.projectIdValue, subs: data.reminded })
      this._apply(data, { modal: "addenda", step: "list" })
    } catch (err) { span.replaceWith(btn); this._toast(err.message, "error") }
  }

  // ── Drop their bid ────────────────────────────────────────────────────
  openDrop(e) {
    const row = e.currentTarget.closest("[data-bidder-id]")
    const b = (this.d.bidders || {})[row.dataset.bidderId]
    if (!b) return
    this.dr = { id: row.dataset.bidderId, b, files: [], pkg: this._pkg(b.package_id) }
    const m = this._modal("drop")
    m.querySelector("[data-dr='title']").textContent = this._t("dr_title", { name: b.name, code: `${b.code} ${b.pkg}`.trim() })
    this._drStep("upload")
    this._renderDropFiles()
    m.classList.remove("hidden")
  }

  _drStep(step) {
    const m = this._modal("drop")
    m.querySelectorAll("[data-dr-step]").forEach(s => s.classList.toggle("hidden", s.dataset.drStep !== step))
    m.querySelector("[data-dr='sub']").textContent = this._t(`dr_sub_${step}`)
  }

  dropPick() { this.dropFileTarget.click() }
  dropPicked(e) { this._dropUpload(Array.from(e.target.files || [])); e.target.value = "" }
  dropDrop(e) { e.preventDefault(); this.dzLeave(e); this._dropUpload(Array.from(e.dataTransfer?.files || [])) }

  _renderDropFiles() {
    const m = this._modal("drop")
    const box = m.querySelector("[data-dr='files']")
    box.innerHTML = this.dr.files.map((f, i) => this._chip(f, String(i))).join("")
    box.querySelectorAll("[data-remove]").forEach(b => b.addEventListener("click", () => { this.dr.files.splice(parseInt(b.dataset.remove, 10), 1); this._renderDropFiles() }))
    m.querySelector("[data-dr='read']").disabled = !this.dr.files.length || this.dr.files.some(f => f.up)
  }

  async _dropUpload(files) {
    for (const file of files.slice(0, 3)) {
      const f = { name: file.name, size: file.size, up: true }
      this.dr.files.push(f)
      this._renderDropFiles()
      try { f.upload = await this._upload(file); f.up = false } catch (err) { this.dr.files = this.dr.files.filter(x => x !== f); this._toast(err.message, "error") }
      this._renderDropFiles()
    }
  }

  async dropRead() {
    const m = this._modal("drop")
    this._drStep("reading")
    const lines = this.str.dr_read_lines || []
    const file = this.dr.files[0]?.name || ""
    let idx = 0
    const paint = () => {
      m.querySelector("[data-dr='readLines']").innerHTML = lines.map((t, i) => `<div class="findline ${i < idx ? "done" : (i === idx ? "now" : "")}">${i < idx ? `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m4.5 12.75 6 6 9-13.5"></path></svg>` : `<span class="dot"></span>`}<span>${this._esc(t.replace("%{file}", file))}</span></div>`).join("")
    }
    paint()
    clearInterval(this._ticker)
    this._ticker = setInterval(() => { if (idx < lines.length - 1) { idx++; paint() } }, 1800)
    try {
      const run = await this._req("POST", `bidders/${this.dr.id}/drop_bid/read`, { files: this.dr.files.map(f => f.upload) })
      await this._finishDropRead(run.run_id)
    } catch (err) {
      clearInterval(this._ticker)
      this._toast(err.message, "error")
      this._drStep("upload")
    }
  }

  async _finishDropRead(runId) {
    this._pendingRun = { kind: "dr", runId }
    try {
      const res = await this._pollRun(runId)
      this._pendingRun = null
      clearInterval(this._ticker)
      this._renderDropReview(res.bid || {})
    } catch (err) {
      this._pendingRun = null
      clearInterval(this._ticker)
      this._toast(err.message, "error")
      this._drStep("upload")
    }
  }

  _renderDropReview(bid) {
    const m = this._modal("drop")
    const file = this.dr.files[0]?.name || ""
    m.querySelector("[data-dr='readNote']").textContent = bid.ai === false ? this._t("dr_no_ai") : this._t("dr_read_note", { file })
    const lines = this.dr.pkg?.lines || []
    const amounts = bid.lines || {}
    const fmt = (v) => (v === null || v === undefined || v === "" ? "" : Number(v).toLocaleString("en-US"))
    m.querySelector("[data-dr='lines']").innerHTML =
      `<div class="drline head"><span>${this._esc(this._t("dr_your_scope"))}</span><span class="r">${this._esc(this._t("dr_their_price"))}</span><span class="r">${this._esc(this._t("dr_for_comparison"))}</span></div>` +
      lines.map(l => `<div class="drline" data-line="${l.id}"><span class="drname"><span>${this._esc(l.name)}</span>${amounts[l.id] ? "" : `<span class="badge b-amber" data-miss>${this._esc(this._t("dr_not_mentioned"))}</span>`}</span><span class="money drmoney"><span>$</span><input class="inp w120" type="text" inputmode="decimal" value="${fmt(amounts[l.id])}" placeholder="${this._esc(this._t("dr_not_priced"))}" aria-label="${this._esc(this._t("dr_their_price_for", { name: l.name }))}"></span><span class="muted r">${l.est ? this._esc(this._t("dr_ai_estimate", { amount: l.est })) : ""}</span></div>`).join("") +
      `<div class="drline tot"><span>${this._esc(this._t("dr_their_total"))}</span><span class="r" data-dr="total"></span><span></span></div>`
    m.querySelectorAll("[data-dr='lines'] input").forEach(i => i.addEventListener("input", () => this._dropTotal()))
    this.dr.inc = bid.inclusions || ""
    this.dr.exc = bid.exclusions || ""
    m.querySelector("[data-dr='inc']").textContent = this._t("dr_their_note", { note: this.dr.inc || this._t("dr_no_note") })
    m.querySelector("[data-dr='exc']").textContent = this._t("dr_their_note", { note: this.dr.exc || this._t("dr_no_note") })
    this._dropTotal()
    this._drStep("review")
  }

  _num(v) { const n = parseFloat(String(v || "").replace(/[$,\s]/g, "")); return isNaN(n) ? 0 : n }

  _dropTotal() {
    const m = this._modal("drop")
    let total = 0, miss = false
    m.querySelectorAll("[data-dr='lines'] .drline[data-line]").forEach(r => { const v = r.querySelector("input").value; total += this._num(v); if (!String(v).trim()) miss = true })
    m.querySelector("[data-dr='total']").textContent = `$${Math.round(total).toLocaleString("en-US")}`
    m.querySelector("[data-dr='miss']").classList.toggle("hidden", !miss)
    m.querySelector("[data-dr='save']").disabled = total <= 0
  }

  dropBack() { this._drStep("upload") }

  async dropSave(e) {
    const m = this._modal("drop")
    const lines = {}
    let total = 0
    m.querySelectorAll("[data-dr='lines'] .drline[data-line]").forEach(r => { const v = r.querySelector("input").value; if (String(v).trim()) { lines[r.dataset.line] = this._num(v); total += this._num(v) } })
    e.currentTarget.disabled = true
    try {
      const data = await this._req("POST", `bidders/${this.dr.id}/drop_bid`, { amount: total, lines, inclusions: this.dr.inc, exclusions: this.dr.exc, files: this.dr.files.map(f => f.upload).filter(Boolean) })
      this._closeModal(m)
      this._toast(this._t("dr_added", { name: this.dr.b.name }))
      track("Precon Bid Dropped In", { project_id: this.projectIdValue, lines: Object.keys(lines).length })
      this._apply(data)
    } catch (err) {
      e.currentTarget.disabled = false
      this._toast(err.message, "error")
    }
  }

  // ── Notes for one trade ───────────────────────────────────────────────
  openTradeNote(e) {
    const ds = e.currentTarget.dataset
    const editing = !!ds.code
    this.tn = { editing, code: ds.code || "", pid: ds.packageId || "", wasEvery: editing && ds.every === "1", lines: editing ? JSON.parse(ds.lines || "[]") : [""] }
    if (!this.tn.lines.length) this.tn.lines = [""]
    const m = this._modal("tn")
    const g = (k) => m.querySelector(`[data-tn='${k}']`)
    g("title").textContent = editing ? ds.title : this._t("tn_add_title")
    g("note").value = editing ? (ds.note || "") : ""
    g("every").checked = editing ? ds.every === "1" : true
    g("remove").classList.toggle("hidden", !editing)
    g("picking").classList.toggle("hidden", editing)
    if (!editing) {
      const picks = (this.d.packages || []).filter(p => !p.noted && p.code && !p.code.startsWith("01"))
      g("picks").innerHTML = picks.map(p => `<button class="fchip" type="button" data-pid="${p.id}" data-code="${this._esc(p.code)}">${this._esc(p.label)}</button>`).join("")
      g("picks").querySelectorAll(".fchip").forEach(c => c.addEventListener("click", () => {
        this.tn.pid = c.dataset.pid; this.tn.code = c.dataset.code
        g("picks").querySelectorAll(".fchip").forEach(x => x.classList.toggle("on", x === c))
        this.tnCheck()
      }))
    }
    this._renderTnLines()
    this._open("tn")
  }

  _renderTnLines() {
    const m = this._modal("tn")
    const box = m.querySelector("[data-tn='lines']")
    const x = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 18 18 6M6 6l12 12"></path></svg>`
    box.innerHTML = this.tn.lines.map((l, i) => `<div class="tnlrow"><input class="inp" type="text" value="${this._esc(l)}" aria-label="${this._esc(this._t("tn_line_aria"))}" data-i="${i}"><button class="setx" type="button" data-i="${i}" aria-label="${this._esc(this._t("tn_remove_line"))}">${x}</button></div>`).join("")
    box.querySelectorAll("input").forEach(inp => inp.addEventListener("input", () => { this.tn.lines[parseInt(inp.dataset.i, 10)] = inp.value; this.tnCheck() }))
    box.querySelectorAll(".setx").forEach(b => b.addEventListener("click", () => { this.tn.lines.splice(parseInt(b.dataset.i, 10), 1); this._renderTnLines() }))
    this.tnCheck()
  }

  tnAddLine() { this.tn.lines.push(""); this._renderTnLines(); const inputs = this._modal("tn").querySelectorAll("[data-tn='lines'] input"); inputs[inputs.length - 1]?.focus() }

  tnCheck() {
    const m = this._modal("tn")
    const note = m.querySelector("[data-tn='note']").value.trim()
    const any = note || this.tn.lines.some(l => String(l).trim())
    m.querySelector("[data-tn='save']").disabled = !this.tn.code || !any
  }

  async tnSave() {
    const m = this._modal("tn")
    const body = { code: this.tn.code, note: m.querySelector("[data-tn='note']").value, lines: this.tn.lines.map(l => String(l).trim()).filter(Boolean), every_job: m.querySelector("[data-tn='every']").checked ? "1" : "0", was_every: this.tn.wasEvery ? "1" : "0" }
    this._closeModal(m)
    try {
      const data = await this._req("POST", "trade_notes", body)
      this._toast(this._t("tn_saved"))
      track("Precon Trade Note Saved", { project_id: this.projectIdValue, every_job: body.every_job === "1", lines: body.lines.length })
      this._apply(data)
    } catch (err) { this._toast(err.message, "error") }
  }

  async tnRemove() {
    const m = this._modal("tn")
    this._closeModal(m)
    try {
      const data = await this._req("DELETE", "trade_notes", { code: this.tn.code, every: this.tn.wasEvery ? "1" : "0" })
      this._toast(this._t("tn_removed"))
      track("Precon Trade Note Removed", { project_id: this.projectIdValue })
      this._apply(data)
    } catch (err) { this._toast(err.message, "error") }
  }

  // ── Add an email (a picked sub with none on file) ────────────────────
  openAddEmail(e) {
    this.ae = { id: e.currentTarget.dataset.subId, name: e.currentTarget.dataset.subName }
    const m = this._modal("email_add")
    m.querySelector("[data-ae='title']").textContent = this._t("ae_title", { name: this.ae.name })
    m.querySelector("[data-ae='email']").value = ""
    this._open("email_add")
    m.querySelector("[data-ae='email']").focus()
  }

  addEmailKey(e) { if (e.key === "Enter") { e.preventDefault(); this.saveEmail() } }

  async saveEmail() {
    const m = this._modal("email_add")
    const email = m.querySelector("[data-ae='email']").value.trim()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { this._toast(this._t("bad_email"), "error"); return }
    this._closeModal(m)
    try {
      await this.precon._json("PATCH", `/precon_subs/${this.ae.id}`, { email })
      this._toast(this._t("email_added", { name: this.ae.name }))
      track("Precon Sub Email Added", { project_id: this.projectIdValue, from: "step3" })
      const data = await this.precon._json("GET", `${this.baseUrlValue}/panel/3`)
      this._apply(data)
    } catch (err) { this._toast(err.message, "error") }
  }
}
